import { gql, useMutation } from '@apollo/client';
import { useAuth } from '@contexts/AuthContext';
import { BATCH_UPDATE_TALENT_FAVORITES_UPERCUT } from '@gql/talentFavorites';
import { DELETE_TALENT_UPERCUT, UPDATE_TALENT_RATING, UPDATE_TALENT_STATUS } from '@gql/talents';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { MultiSelectTalent } from '../type';
import { getMultiSelectTalentStatus } from '../utils';
import JobOfferSuggestionDialog from '@modules/JobOffers/JobOfferSuggestionDialog';

type UpercutFavoriteType = 'public' | 'private';
type TalentStatus = 'nothing' | 'looking' | 'open' | 'closed' | 'inactive' | 'mixed';

interface NotesDialogState {
    open: boolean;
    talentId: string | null;
    talentName: string | null;
}

interface TalentOperationsContextValue {
    // Selection state (from TalentSelectionContext)
    selectedTalents: MultiSelectTalent[];
    selectedTalentIds: string[]; // Derived property for convenience
    currentStatus: TalentStatus;
    addTalent: (talentId: string, status?: string) => void;
    removeTalent: (talentId: string) => void;
    toggleTalent: (talentId: string, status?: string) => void;
    clearSelection: () => void;
    isSelected: (talentId: string) => boolean;

    // Operations (from TalentManagerContext)
    updateStatus: (talentIds: string[], status: string) => void;
    updateRating: (talentIds: string[], rating: number) => void;
    deleteTalent: (talentId: string) => Promise<void>;
    updateFavorites: (
        talentIds: string[],
        currentFavorites: Array<{ _id: string; type: string; upercutId?: string; createdAt: string; }>,
        initialTypes: UpercutFavoriteType[],
        newTypes: UpercutFavoriteType[]
    ) => Promise<void>;
    showSuggestJobOfferDialog: (talentIds?: string[]) => void;
    hideSuggestJobOfferDialog: () => void;

    // Loading states
    isUpdatingStatus: boolean;
    isUpdatingRating: boolean;
    isDeletingTalent: boolean;
    isUpdatingFavorites: boolean;

    // Notes dialog
    notesDialog: NotesDialogState;
    openNotesDialog: (talentId: string, talentName: string) => void;
    closeNotesDialog: () => void;

    // Convenience methods for operating on selected talents
    updateSelectedStatus: (status: string) => void;
    updateSelectedStatusFromAvatar: (avatarStatus: string) => void; // Converts avatar status to backend format
    updateSelectedRating: (rating: number) => void;
    updateSelectedFavorites: (
        currentFavorites: Array<{ _id: string; type: string; upercutId?: string; createdAt: string; }>,
        initialTypes: UpercutFavoriteType[],
        newTypes: UpercutFavoriteType[]
    ) => Promise<void>;
}

const TalentOperationsContext = createContext<TalentOperationsContextValue | null>(null);

export const useTalentOperations = (): TalentOperationsContextValue => {
    const ctx = useContext(TalentOperationsContext);
    if (!ctx) {
        throw new Error('useTalentOperations must be used within a <TalentOperationsProvider>');
    }
    return ctx;
};

interface TalentOperationsProviderProps {
    children: React.ReactNode;
}

export const TalentOperationsProvider: React.FC<TalentOperationsProviderProps> = ({
    children,
}) => {
    const { showSnackbar } = useSnackbar();
    const { isUpercut } = useAuth();

    // Selection state (from TalentSelectionContext)
    const [selectedTalents, setSelectedTalents] = useState<MultiSelectTalent[]>([]);
    const selectedTalentIds = useMemo(() => selectedTalents.map(t => t._id), [selectedTalents]);
    const currentStatus = useMemo(() => getMultiSelectTalentStatus(selectedTalents), [selectedTalents]);

    // Notes dialog state
    const [notesDialog, setNotesDialog] = useState<NotesDialogState>({
        open: false,
        talentId: null,
        talentName: null,
    });

    // Job offer suggestion dialog state
    const [jobOfferDialogOpen, setJobOfferDialogOpen] = useState(false);
    const [jobOfferDialogTalentIds, setJobOfferDialogTalentIds] = useState<string[]>([]);

    // Mutations
    const [updateStatusMutation, { loading: isUpdatingStatus }] = useMutation(UPDATE_TALENT_STATUS);
    const [updateRatingMutation, { loading: isUpdatingRating }] = useMutation(UPDATE_TALENT_RATING);
    const [deleteTalentMutation, { loading: isDeletingTalent }] = useMutation(DELETE_TALENT_UPERCUT);
    const [batchUpdateFavoritesUpercut, { loading: isUpdatingFavorites }] = useMutation(BATCH_UPDATE_TALENT_FAVORITES_UPERCUT);

    // Selection methods
    const addTalent = useCallback((talentId: string, status?: string) => {
        setSelectedTalents(prev => {
            const isAlreadySelected = prev.some(selected => selected._id === talentId);
            if (isAlreadySelected) return prev;
            return [...prev, { _id: talentId, status }];
        });
    }, []);

    const removeTalent = useCallback((talentId: string) => {
        setSelectedTalents(prev => prev.filter(selected => selected._id !== talentId));
    }, []);

    const toggleTalent = useCallback((talentId: string, status?: string) => {
        setSelectedTalents(prev => {
            const isSelected = prev.some(selected => selected._id === talentId);
            if (isSelected) {
                return prev.filter(selected => selected._id !== talentId);
            } else {
                return [...prev, { _id: talentId, status }];
            }
        });
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedTalents([]);
    }, []);

    const isSelected = useCallback((talentId: string) => {
        return selectedTalents.some(selected => selected._id === talentId);
    }, [selectedTalents]);

    // Notes dialog methods
    const openNotesDialog = useCallback((talentId: string, talentName: string) => {
        setNotesDialog({ open: true, talentId, talentName });
    }, []);

    const closeNotesDialog = useCallback(() => {
        setNotesDialog({ open: false, talentId: null, talentName: null });
    }, []);

    const hideSuggestJobOfferDialog = useCallback(() => {
        setJobOfferDialogOpen(false);
    }, []);

    const showSuggestJobOfferDialog = useCallback((talentIds?: string[]) => {
        const targetTalentIds = talentIds && talentIds.length > 0 ? talentIds : selectedTalentIds;

        if (targetTalentIds.length === 0) {
            showSnackbar('Please select at least one talent', 'warning');
            return;
        }

        setJobOfferDialogTalentIds(targetTalentIds);
        setJobOfferDialogOpen(true);
    }, [selectedTalentIds, showSnackbar]);

    // Operation methods
    const updateStatus = useCallback(
        (talentIds: string[], status: string) => {
            updateStatusMutation({
                variables: {
                    payload: {
                        talentIds,
                        currentStatusUser: status,
                    },
                },
                update: (cache) => {
                    // Update both Talent and TalentPublic entities in cache.
                    // JobApplication rows usually reference TalentPublic.
                    talentIds.forEach(talentId => {
                        const talentCacheId = cache.identify({ __typename: 'Talent', _id: talentId });
                        if (talentCacheId) {
                            console.log("talentCacheId", talentCacheId)
                            cache.modify({
                                id: talentCacheId,
                                fields: {
                                    currentStatusUser() {
                                        return status;
                                    },
                                },
                            });
                        }

                        const talentPublicCacheId = cache.identify({ __typename: 'TalentPublic', _id: talentId });
                        if (talentPublicCacheId) {
                            console.log("talentPublicCacheId", talentPublicCacheId)
                            cache.modify({
                                id: talentPublicCacheId,
                                fields: {
                                    currentStatusUser() {
                                        return status;
                                    },
                                },
                            });
                        }
                    });
                },
                onCompleted: () => {
                    showSnackbar(
                        `Talent status${talentIds.length > 1 ? 'es' : ''} updated successfully`,
                        'success'
                    );
                },
                onError: (error) => {
                    showSnackbar(error.message, 'error');
                },
            });
        },
        [updateStatusMutation, showSnackbar]
    );

    const updateRating = useCallback(
        (talentIds: string[], rating: number) => {
            updateRatingMutation({
                variables: {
                    talentIds,
                    rating,
                },
                update: (cache, { data }) => {
                    if (data?.updateTalentRatingUpercut) {
                        // Update each talent entity directly in the cache
                        data.updateTalentRatingUpercut.forEach((updatedTalent: any) => {
                            cache.modify({
                                id: cache.identify({ __typename: 'Talent', _id: updatedTalent._id }),
                                fields: {
                                    upercutAverageRating() {
                                        return updatedTalent.upercutAverageRating;
                                    },
                                    upercutRatings() {
                                        return updatedTalent.upercutRatings;
                                    },
                                },
                            });
                        });
                    }
                },
                onCompleted: () => {
                    showSnackbar(
                        `Talent rating${talentIds.length > 1 ? 's' : ''} updated successfully`,
                        'success'
                    );
                },
                onError: (error) => {
                    showSnackbar(error.message, 'error');
                },
            });
        },
        [updateRatingMutation, showSnackbar]
    );

    const deleteTalent = useCallback(
        async (talentId: string) => {
            try {
                await deleteTalentMutation({
                    variables: { talentId },
                    refetchQueries: ['GetAllActiveTalents', 'GetTalentFavoritesUpercut'],
                    update: (cache) => {
                        const talentCacheId = cache.identify({ __typename: 'Talent', _id: talentId });
                        if (talentCacheId) {
                            cache.evict({ id: talentCacheId });
                        }

                        const talentPublicCacheId = cache.identify({ __typename: 'TalentPublic', _id: talentId });
                        if (talentPublicCacheId) {
                            cache.evict({ id: talentPublicCacheId });
                        }

                        cache.evict({ fieldName: 'getTalentsUpercut' });
                        cache.evict({ fieldName: 'getTalentFavoritesUpercut' });
                        cache.evict({ fieldName: 'searchTalents' });
                        cache.evict({ fieldName: 'searchTalentFilters' });
                        cache.gc();
                    },
                });

                removeTalent(talentId);
                showSnackbar('Talent deleted successfully', 'success');
            } catch (error) {
                showSnackbar(error.message, 'error');
                throw error;
            }
        },
        [deleteTalentMutation, removeTalent, showSnackbar]
    );

    const updateFavorites = useCallback(
        async (
            talentIds: string[],
            currentFavorites: Array<{ _id: string; type: string; upercutId?: string; createdAt: string; }>,
            initialTypes: UpercutFavoriteType[],
            newTypes: UpercutFavoriteType[]
        ) => {
            if (!isUpercut) {
                console.warn('Attempted to update talent favorite for non-Upercut user');
                return;
            }

            try {
                // Determine what to add and remove
                const toAdd = newTypes.filter(type => !initialTypes.includes(type));
                const toRemove = initialTypes.filter(type => !newTypes.includes(type));

                // Get current favorite IDs for removal operations
                const currentFavoriteIds = currentFavorites.map(fav => fav._id);

                // Use batch update mutation to handle add/remove atomically
                await batchUpdateFavoritesUpercut({
                    variables: {
                        input: {
                            talentIds,
                            currentFavoriteIds,
                            toAdd: toAdd.length > 0 ? toAdd : undefined,
                            toRemove: toRemove.length > 0 ? toRemove : undefined,
                            metadata: undefined // Add metadata if needed
                        }
                    },
                    refetchQueries: ["GetTalentFavoritesUpercut"],
                    update: (cache, { data }) => {
                        if (data?.batchUpdateTalentFavoritesUpercut) {
                            const { added, removed } = data.batchUpdateTalentFavoritesUpercut;

                            // Update cache for each talent
                            talentIds.forEach(talentId => {
                                cache.modify({
                                    id: cache.identify({ __typename: 'TalentPublic', _id: talentId }),
                                    fields: {
                                        upercutFavorites(existingFavorites = []) {
                                            let updatedFavorites = [...existingFavorites];

                                            // Remove deleted favorites
                                            if (removed.length > 0) {
                                                updatedFavorites = updatedFavorites.filter((favRef: any) => {
                                                    const favId = favRef.__ref?.split(':')[1];
                                                    return !removed.includes(favId);
                                                });
                                            }

                                            // Add new favorites
                                            if (added.length > 0) {
                                                const newFavRefs = added.map((fav: any) => {
                                                    const transformedFav = {
                                                        _id: fav._id,
                                                        talentId,
                                                        type: fav.type,
                                                        metadata: fav.metadata,
                                                        createdAt: fav.createdAt,
                                                        __typename: 'TalentFavorite'
                                                    };

                                                    return cache.writeFragment({
                                                        data: transformedFav,
                                                        fragment: gql`
                                                            fragment NewFavorite on TalentFavorite {
                                                                _id
                                                                talentId
                                                                type
                                                                metadata {
                                                                    notes
                                                                    tags
                                                                    priority
                                                                    campaign
                                                                }
                                                                createdAt
                                                            }
                                                        `
                                                    });
                                                });
                                                updatedFavorites = [...updatedFavorites, ...newFavRefs];
                                            }

                                            return updatedFavorites;
                                        },
                                    },
                                });
                                cache.modify({
                                    id: cache.identify({ __typename: 'Talent', _id: talentId }),
                                    fields: {
                                        upercutFavorites(existingFavorites = []) {
                                            let updatedFavorites = [...existingFavorites];

                                            // Remove deleted favorites
                                            if (removed.length > 0) {
                                                updatedFavorites = updatedFavorites.filter((favRef: any) => {
                                                    const favId = favRef.__ref?.split(':')[1];
                                                    return !removed.includes(favId);
                                                });
                                            }

                                            // Add new favorites
                                            if (added.length > 0) {
                                                const newFavRefs = added.map((fav: any) => {
                                                    const transformedFav = {
                                                        _id: fav._id,
                                                        talentId,
                                                        type: fav.type,
                                                        metadata: fav.metadata,
                                                        createdAt: fav.createdAt,
                                                        __typename: 'TalentFavorite'
                                                    };

                                                    return cache.writeFragment({
                                                        data: transformedFav,
                                                        fragment: gql`
                                                            fragment NewFavorite on TalentFavorite {
                                                                _id
                                                                talentId
                                                                type
                                                                metadata {
                                                                    notes
                                                                    tags
                                                                    priority
                                                                    campaign
                                                                }
                                                                createdAt
                                                            }
                                                        `
                                                    });
                                                });
                                                updatedFavorites = [...updatedFavorites, ...newFavRefs];
                                            }

                                            return updatedFavorites;
                                        },
                                    },
                                });
                            });
                        }

                        cache.evict({ fieldName: 'searchTalents' });
                        cache.evict({ fieldName: 'searchTalentFilters' });
                        cache.evict({ fieldName: 'getTalentFavoritesUpercut' });
                        cache.evict({ fieldName: 'checkTalentFavoriteStatusUpercut' });
                        cache.gc();
                    }
                });

                showSnackbar(
                    `Talent favorite${talentIds.length > 1 ? 's' : ''} updated successfully`,
                    'success'
                );
            } catch (error) {
                console.error('Error updating favorite:', error);
                showSnackbar('Error updating favorites', 'error');
            }
        },
        [batchUpdateFavoritesUpercut, showSnackbar, isUpercut]
    );

    // Convenience methods for operating on selected talents
    const updateSelectedStatus = useCallback((status: string) => {
        const talentIds = selectedTalents.map(t => t._id);
        updateStatus(talentIds, status);
    }, [selectedTalents, updateStatus]);

    const updateSelectedStatusFromAvatar = useCallback((avatarStatus: string) => {
        const backendStatus = avatarStatus === 'nothing' ? '' : avatarStatus;
        updateSelectedStatus(backendStatus);
    }, [updateSelectedStatus]);

    const updateSelectedRating = useCallback((rating: number) => {
        const talentIds = selectedTalents.map(t => t._id);
        updateRating(talentIds, rating);
    }, [selectedTalents, updateRating]);

    const updateSelectedFavorites = useCallback(async (
        currentFavorites: Array<{ _id: string; type: string; upercutId?: string; createdAt: string; }>,
        initialTypes: UpercutFavoriteType[],
        newTypes: UpercutFavoriteType[]
    ) => {
        const talentIds = selectedTalents.map(t => t._id);
        return updateFavorites(talentIds, currentFavorites, initialTypes, newTypes);
    }, [selectedTalents, updateFavorites]);

    const value = useMemo<TalentOperationsContextValue>(
        () => ({
            // Selection state
            selectedTalents,
            selectedTalentIds,
            currentStatus,
            addTalent,
            removeTalent,
            toggleTalent,
            clearSelection,
            isSelected,

            // Operations
            updateStatus,
            updateRating,
            deleteTalent,
            updateFavorites,
            showSuggestJobOfferDialog,
            hideSuggestJobOfferDialog,

            // Loading states
            isUpdatingStatus,
            isUpdatingRating,
            isDeletingTalent,
            isUpdatingFavorites,

            // Notes dialog
            notesDialog,
            openNotesDialog,
            closeNotesDialog,

            // Convenience methods
            updateSelectedStatus,
            updateSelectedStatusFromAvatar,
            updateSelectedRating,
            updateSelectedFavorites,
        }),
        [
            selectedTalents,
            selectedTalentIds,
            currentStatus,
            addTalent,
            removeTalent,
            toggleTalent,
            clearSelection,
            isSelected,
            updateStatus,
            updateRating,
            deleteTalent,
            updateFavorites,
            showSuggestJobOfferDialog,
            hideSuggestJobOfferDialog,
            isUpdatingStatus,
            isUpdatingRating,
            isDeletingTalent,
            isUpdatingFavorites,
            notesDialog,
            openNotesDialog,
            closeNotesDialog,
            updateSelectedStatus,
            updateSelectedStatusFromAvatar,
            updateSelectedRating,
            updateSelectedFavorites,
        ]
    );

    return <TalentOperationsContext.Provider value={value}>
        {children}
        <JobOfferSuggestionDialog
            open={jobOfferDialogOpen}
            setOpen={setJobOfferDialogOpen}
            talentIds={jobOfferDialogTalentIds}
            refetchQueries={['GET_ALL_ACTIVE_TALENTS_QUERY']}
        />
    </TalentOperationsContext.Provider>;
};
